<template>
  <div style="padding: 24px;">
    <a-card :bordered="false" title="英皇线上代报名订单管理">
      <!-- 搜索与筛选区域 -->
      <div style="margin-bottom: 24px; display: flex; flex-wrap: wrap; gap: 16px; align-items: center;">
        <a-input-search
          v-model="queryParams.content"
          placeholder="搜索姓名 / 手机号 / Contact ID"
          style="width: 250px"
          enter-button
          @search="handleSearch"
        />

        <a-select v-model="queryParams.exam_type" style="width: 180px" placeholder="报考类别" @change="handleSearch">
          <a-select-option value="">全部类别</a-select-option>
          <a-select-option :value="1">线上乐理 / Music Theory</a-select-option>
          <a-select-option :value="2">线上演奏 / Performance Grades</a-select-option>
        </a-select>

        <a-select v-model="queryParams.status" style="width: 150px" placeholder="审核状态" @change="handleSearch">
          <a-select-option value="">全部状态</a-select-option>
          <a-select-option :value="0">待审核</a-select-option>
          <a-select-option :value="1">代报中 (审核通过)</a-select-option>
          <a-select-option :value="2">代报成功</a-select-option>
          <a-select-option :value="3">已拒绝</a-select-option>
        </a-select>

        <a-button type="primary" icon="search" @click="fetchData">
          查询
        </a-button>
        <a-button icon="reload" @click="resetQuery">
          重置
        </a-button>
        
        <!-- 一键导出批量报考 Excel -->
        <a-button type="danger" icon="download" @click="exportToExcel" style="margin-left: auto; background-color: #ff5f4e; border-color: #ff5f4e;">
          导出官方批量导入表 (代报中)
        </a-button>
      </div>

      <!-- 表格数据区域 -->
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :row-key="record => record.id"
        :pagination="pagination"
        :loading="loading"
        @change="handleTableChange"
        bordered
      >
        <span slot="exam_type" slot-scope="text">
          <a-tag :color="text === 1 ? 'purple' : 'cyan'">
            {{ text === 1 ? '线上乐理 (Theory)' : '线上演奏 (Grades)' }}
          </a-tag>
        </span>

        <span slot="level_info" slot-scope="text, record">
          <div>{{ record.exam_level }}</div>
          <div style="font-size: 12px; color: #888;">
            {{ record.exam_type === 1 ? '乐理 (Theory)' : record.instrument }}
          </div>
        </span>

        <span slot="name_zh" slot-scope="text, record">
          <div><strong>{{ text }}</strong></div>
          <div style="font-size: 12px; color: #888;">{{ record.name_en }}</div>
        </span>

        <span slot="birth_gender" slot-scope="text, record">
          <div>{{ record.birthday }}</div>
          <div style="font-size: 12px; color: #888;">
            {{ record.gender === 1 ? '男 (Male)' : '女 (Female)' }}
          </div>
        </span>

        <span slot="id_type" slot-scope="text, record">
          <div>{{ getIdTypeText(text) }}</div>
          <div style="font-size: 12px; color: #888;">{{ record.id_number }}</div>
        </span>

        <span slot="pay_status" slot-scope="text, record">
          <div><strong>￥{{ parseFloat(record.pay_price || 0).toFixed(2) }}</strong></div>
          <div>
            <a-badge :status="text === 1 ? 'success' : 'error'" :text="text === 1 ? '已支付' : '未支付'" />
          </div>
        </span>

        <span slot="materials" slot-scope="text, record">
          <div v-if="record.exam_type === 2">
            <div style="margin-bottom: 4px;">
              <span v-if="record.video_url">
                <a-tag color="blue" style="cursor: pointer;" @click="viewVideo(record)">
                  <a-icon type="video-camera" /> 演奏视频
                </a-tag>
              </span>
              <span v-else style="color: #bfbfbf; font-size: 12px;">暂无视频</span>
            </div>
            <div>
              <span v-if="record.song_list_sheet">
                <a-tag color="green" style="cursor: pointer;" @click="viewSongList(record)">
                  <a-icon type="file-image" /> 曲目表
                </a-tag>
              </span>
              <span v-else style="color: #bfbfbf; font-size: 12px;">暂无曲目单</span>
            </div>
          </div>
          <span v-else style="color: #ccc;">—</span>
        </span>

        <span slot="exam_date" slot-scope="text, record">
          <div v-if="record.status === 2 && record.exam_result" style="color: #1890ff; font-weight: 600;" :title="'成绩: ' + record.exam_result">
            {{ text || '—' }} ({{ record.exam_result }})
          </div>
          <div v-else-if="record.status === 2">
            {{ text || '—' }} (成绩待定)
          </div>
          <div v-else>
            {{ text || '—' }}
          </div>
        </span>

        <span slot="status" slot-scope="text">
          <a-badge :status="getBadgeStatus(text)" :text="getStatusText(text)" />
        </span>

        <span slot="action" slot-scope="text, record">
          <!-- 待审核订单或已拒绝订单：展示“核验比对” -->
          <a v-if="record.status === 0 || record.status === 3" @click="openAuditModal(record)">
            <a-icon type="solution" /> 核验比对
          </a>
          <!-- 代报中或代报成功：展示“登记/出分回填” -->
          <a v-if="record.status === 1 || record.status === 2" @click="openCompleteModal(record)" style="color: #2e7d32;">
            <a-icon type="edit" /> 登记/出分回填
          </a>
          <a-divider type="vertical" />
          
          <a-popconfirm
            v-if="record.status === 0 || record.status === 3"
            title="确认要删除此报考申请记录吗？"
            ok-text="确认"
            cancel-text="取消"
            @confirm="handleDelete(record.id)"
          >
            <a style="color: #ff4d4f;"><a-icon type="delete" /> 删除</a>
          </a-popconfirm>
          <span v-else style="color: #bfbfbf; cursor: not-allowed;" title="已通过审核的订单无法直接删除 / Cannot delete processed orders"><a-icon type="delete" /> 删除</span>
        </span>
      </a-table>
    </a-card>

    <!-- 1. 双排对比审核 Modal -->
    <a-modal
      title="报考身份信息双排对比核验"
      :visible="auditVisible"
      :width="900"
      @cancel="auditVisible = false"
      :footer="null"
      centered
    >
      <div v-if="activeRecord" class="audit-modal-body">
        <a-row :gutter="24">
          <!-- 左侧大图：证件照 + 证书照 -->
          <a-col :span="12">
            <div class="media-container">
              <div class="media-section-title">考生证件照 / ID Card Photo</div>
              <a :href="activeRecord.id_card_image" target="_blank" title="在新窗口查看大图">
                <img :src="activeRecord.id_card_image" class="audit-img" />
              </a>
              <div class="media-tip">提示：点击图片可在新标签页查看全尺寸原图</div>

              <!-- 仅在演奏6-8级时展示证书照片 -->
              <template v-if="isTheoryCertRequired(activeRecord)">
                <div class="media-section-title" style="margin-top: 20px;">乐理5级合格证书 / Grade 5 Theory Certificate</div>
                <a v-if="activeRecord.theory_cert_image" :href="activeRecord.theory_cert_image" target="_blank">
                  <img :src="activeRecord.theory_cert_image" class="audit-img" />
                </a>
                <div v-else class="cert-missing-alert">
                  警告：报考演奏高等级，但未检测到前置5级乐理证书！
                </div>
              </template>
            </div>
          </a-col>

          <!-- 右侧：大字身份信息录入明细 -->
          <a-col :span="12">
            <div class="info-container">
              <div class="info-header">录入信息核对列表 / Candidate Info</div>

              <div class="info-row">
                <div class="label">中文姓名:</div>
                <div class="val highlight">{{ activeRecord.name_zh }}</div>
              </div>

              <div class="info-row">
                <div class="label">英文拼音:</div>
                <div class="val highlight pinyin">{{ activeRecord.name_en }}</div>
                <div class="val-tip">（请仔细核对是否与证件上英文/拼音完全一致）</div>
              </div>

              <div class="info-row">
                <div class="label">出生日期:</div>
                <div class="val highlight">{{ activeRecord.birthday }}</div>
                <div class="val-tip">（请比对证件上对应的年、月、日）</div>
              </div>

              <div class="info-row">
                <div class="label">性别:</div>
                <div class="val">{{ activeRecord.gender === 1 ? '男 (Male)' : '女 (Female)' }}</div>
              </div>

              <div class="info-row">
                <div class="label">证件号码:</div>
                <div class="val">{{ getIdTypeText(activeRecord.id_type) }} - <strong style="font-size: 15px;">{{ activeRecord.id_number }}</strong></div>
              </div>

              <div class="info-row">
                <div class="label">报考科目 & 乐器:</div>
                <div class="val">
                  <a-tag :color="activeRecord.exam_type === 1 ? 'purple' : 'cyan'">
                    {{ activeRecord.exam_type === 1 ? '线上乐理 (Theory)' : '线上演奏 (Grades)' }}
                  </a-tag>
                  <strong>{{ activeRecord.instrument }} - {{ activeRecord.exam_level }}</strong>
                </div>
              </div>

              <div class="info-row">
                <div class="label">联系方式:</div>
                <div class="val">手机号: {{ activeRecord.mobile }} / 备用邮箱: {{ activeRecord.email || '无' }}</div>
              </div>

              <a-divider style="margin: 20px 0;" />

              <!-- 操作区 -->
              <div class="audit-action-area" v-if="!rejectMode">
                <a-button type="primary" size="large" icon="check" @click="handleAudit(1)" style="background-color: #2e7d32; border-color: #2e7d32; width: 45%;">
                  审核通过 (开始代报)
                </a-button>
                <a-button type="danger" size="large" icon="close" @click="rejectMode = true" style="width: 45%;">
                  驳回 / 拒绝
                </a-button>
              </div>

              <!-- 驳回输入区 -->
              <div v-else class="reject-input-area">
                <a-textarea v-model="auditRemark" placeholder="请输入拒绝原因 (如：证件上拼音为 ZHANG SAN 但录入为 ZHANG SHAN，请核实)" :rows="3" />
                <div style="margin-top: 12px; display: flex; gap: 12px; justify-content: flex-end;">
                  <a-button @click="rejectMode = false">取消</a-button>
                  <a-button type="danger" :loading="auditLoading" @click="handleAudit(3)">确认驳回</a-button>
                </div>
              </div>

            </div>
          </a-col>
        </a-row>
      </div>
    </a-modal>

    <!-- 2. 登记成功并回填成绩/证书/评分表 Modal -->
    <a-modal
      title="英皇官方报考信息及成绩回填登记"
      :visible="completeVisible"
      @ok="handleCompleteSubmit"
      @cancel="completeVisible = false"
      ok-text="确认并保存"
      cancel-text="取消"
      :width="600"
      :confirm-loading="completeLoading"
      centered
    >
      <div v-if="activeRecord" style="padding: 10px 0;">
        <div style="margin-bottom: 12px; font-size: 14px;">
          正在为考生 <strong>{{ activeRecord.name_zh }} ({{ activeRecord.name_en }})</strong> 登记官方报考结果与出分状态：
        </div>
        
        <a-row :gutter="16">
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">官方 Contact ID / Candidate ID (必填)：</div>
              <a-input v-model="completeForm.contact_id" placeholder="请输入 Contact ID" />
            </div>
          </a-col>
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">官方代报绑定邮箱 (必填)：</div>
              <a-input v-model="completeForm.assigned_email" placeholder="请输入分配绑定的代报邮箱" />
            </div>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">考试日期 / Exam Date (选填)：</div>
              <a-input v-model="completeForm.exam_date" placeholder="例如: 01 十二月 2026 或 待定" />
            </div>
          </a-col>
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">考试成绩结果 / Score Result (选填)：</div>
              <a-input v-model="completeForm.exam_result" placeholder="例如: 125/150 良好" />
            </div>
          </a-col>
        </a-row>

        <a-divider style="margin: 8px 0 16px;" />

        <a-row :gutter="16">
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">上传官方评分表图片 / Score Sheet (选填)：</div>
              <upload-img v-model="completeForm.score_sheet" />
              <div style="font-size: 11px; color: #888; margin-top: 4px;">回填后考生可在前台列表直接查看下载</div>
            </div>
          </a-col>
          <a-col :span="12">
            <div style="margin-bottom: 16px;">
              <div style="margin-bottom: 6px; font-weight: 500;">上传官方电子证书图片 / Certificate (选填)：</div>
              <upload-img v-model="completeForm.cert_image" />
              <div style="font-size: 11px; color: #888; margin-top: 4px;">回填后考生可在前台列表一键查阅下载</div>
            </div>
          </a-col>
        </a-row>
      </div>
    </a-modal>

    <!-- 查看演奏视频 Modal -->
    <a-modal
      title="查看考评演奏视频"
      :visible="videoVisible"
      @cancel="videoVisible = false"
      :footer="null"
      destroyOnClose
      width="600px"
      centered
    >
      <div v-if="activeRecord" style="padding: 10px 0;">
        <div style="margin-bottom: 12px;">
          考生：<strong>{{ activeRecord.name_zh }}</strong>，报考科目：<strong>{{ activeRecord.exam_level }} ({{ activeRecord.instrument }})</strong>
        </div>
        <!-- 视频直传类型 -->
        <div v-if="activeRecord.video_type === 1" style="text-align: center;">
          <video v-if="activeRecord.video_url" :src="activeRecord.video_url" controls style="width: 100%; max-height: 400px; border-radius: 4px; background: #000;" />
          <div v-else style="padding: 40px 0; color: #999;">考生尚未直传视频文件</div>
        </div>
        <!-- 网盘分享链接类型 -->
        <div v-else style="background: #fafafa; border: 1px solid #e8e8e8; border-radius: 4px; padding: 16px;">
          <div style="font-weight: 500; font-size: 14px; margin-bottom: 8px; color: #333;">考生网盘视频提取地址：</div>
          <div style="font-family: monospace; background: #fff; padding: 10px; border: 1px dashed #d9d9d9; border-radius: 4px; word-break: break-all; margin-bottom: 12px; font-size: 13px;">
            {{ activeRecord.video_url || '暂无链接' }}
          </div>
          <div style="display: flex; gap: 10px; justify-content: flex-end;">
            <a-button type="primary" size="small" @click="copyToClipboard(activeRecord.video_url)">
              <a-icon type="copy" /> 复制网盘链接
            </a-button>
            <a-button type="default" size="small" :href="activeRecord.video_url" target="_blank" v-if="activeRecord.video_url">
              <a-icon type="export" /> 浏览器新标签打开
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>

    <!-- 查看曲目单大图 Modal -->
    <a-modal
      title="查看 Candidate Form 曲目单/表"
      :visible="songListVisible"
      @cancel="songListVisible = false"
      :footer="null"
      destroyOnClose
      width="500px"
      centered
    >
      <div v-if="activeRecord" style="text-align: center; padding: 10px 0;">
        <div style="margin-bottom: 12px; text-align: left;">
          考生：<strong>{{ activeRecord.name_zh }}</strong>，已上传的曲目单大图：
        </div>
        <img v-if="activeRecord.song_list_sheet" :src="activeRecord.song_list_sheet" alt="Song List Sheet" style="max-width: 100%; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);" />
        <div v-else style="padding: 40px 0; color: #999;">考生尚未上传曲目单</div>
      </div>
    </a-modal>

  </div>
</template>

<script>
import uploadImg from '@/components/uploadImg'
import { getExamApplyList, auditExamApply, completeExamApply, delExamApply } from '@/libs/examapi'

export default {
  name: 'ExamApplyList',
  components: {
    uploadImg
  },
  data () {
    return {
      loading: false,
      dataSource: [],
      queryParams: {
        content: '',
        exam_type: '',
        status: ''
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条申请`
      },
      columns: [
        { title: '编号', dataIndex: 'id', key: 'id', width: '70px', align: 'center' },
        { title: '报考科目与类别', dataIndex: 'exam_type', key: 'exam_type', scopedSlots: { customRender: 'exam_type' } },
        { title: '乐器/级别', dataIndex: 'exam_level', key: 'exam_level', width: '120px', align: 'center', scopedSlots: { customRender: 'level_info' } },
        { title: '考生姓名 (中/英)', dataIndex: 'name_zh', key: 'name_zh', scopedSlots: { customRender: 'name_zh' } },
        { title: '性别/生日', dataIndex: 'birthday', key: 'birthday', width: '120px', align: 'center', scopedSlots: { customRender: 'birth_gender' } },
        { title: '证件信息', dataIndex: 'id_type', key: 'id_type', scopedSlots: { customRender: 'id_type' } },
        { title: '实付费用/状态', dataIndex: 'pay_status', key: 'pay_status', width: '130px', scopedSlots: { customRender: 'pay_status' } },
        { title: '考评材料', key: 'materials', width: '130px', align: 'center', scopedSlots: { customRender: 'materials' } },
        { title: '考试日期', dataIndex: 'exam_date', key: 'exam_date', width: '110px', align: 'center', scopedSlots: { customRender: 'exam_date' } },
        { title: '状态', dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: '180px', align: 'center' }
      ],

      // 审核相关属性
      auditVisible: false,
      activeRecord: null,
      rejectMode: false,
      auditRemark: '',
      auditLoading: false,

      // 登记成功回填相关属性
      completeVisible: false,
      completeLoading: false,
      completeForm: {
        contact_id: '',
        assigned_email: '',
        exam_date: '',
        exam_result: '',
        score_sheet: '',
        cert_image: ''
      },

      // 考评附件预览相关属性
      videoVisible: false,
      songListVisible: false
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.loading = true
      const params = {
        page: this.pagination.current,
        size: this.pagination.pageSize,
        ...this.queryParams
      }
      getExamApplyList(params)
        .then(res => {
          this.loading = false
          this.dataSource = res.list || []
          this.pagination.total = res.count || 0
        })
        .catch(err => {
          this.loading = false
          console.error(err)
          this.$message.error('加载代报列表失败')
        })
    },
    handleTableChange (pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.fetchData()
    },
    handleSearch () {
      this.pagination.current = 1
      this.fetchData()
    },
    resetQuery () {
      this.queryParams = {
        content: '',
        exam_type: '',
        status: ''
      }
      this.pagination.current = 1
      this.fetchData()
    },
    getIdTypeText (type) {
      const types = {
        1: '身份证',
        2: '护照',
        3: '港澳通行证',
        4: '台湾通行证'
      }
      return types[type] || '其他证件'
    },
    getBadgeStatus (status) {
      const statuses = {
        0: 'warning', // 待审核
        1: 'processing', // 代报中
        2: 'success', // 已成功
        3: 'error' // 已拒绝
      }
      return statuses[status] || 'default'
    },
    getStatusText (status) {
      const texts = {
        0: '待审核',
        1: '代报中 (审核通过)',
        2: '已成功',
        3: '已拒绝'
      }
      return texts[status] || '未知'
    },
    isTheoryCertRequired (record) {
      if (record.exam_type === 2) {
        const levelNum = parseInt(record.exam_level.replace(/[^0-9]/g, ''))
        return levelNum >= 6 && levelNum <= 8
      }
      return false
    },
    openAuditModal (record) {
      this.activeRecord = record
      this.rejectMode = false
      this.auditRemark = ''
      this.auditVisible = true
    },
    handleAudit (status) {
      const params = {
        id: this.activeRecord.id,
        status: status,
        remark: status === 3 ? this.auditRemark : ''
      }

      if (status === 3 && !params.remark.trim()) {
        this.$message.warning('请输入驳回/拒绝原因')
        return
      }

      this.auditLoading = true
      auditExamApply(params)
        .then(() => {
          this.auditLoading = false
          this.auditVisible = false
          this.$message.success(status === 1 ? '审核通过，已转入代报中' : '已成功驳回该报考申请')
          this.fetchData()
        })
        .catch(err => {
          this.auditLoading = false
          console.error(err)
          this.$message.error('审核操作失败')
        })
    },
    openCompleteModal (record) {
      this.activeRecord = record
      this.completeForm = {
        contact_id: record.contact_id || '',
        assigned_email: record.assigned_email || '',
        exam_date: record.exam_date || '',
        exam_result: record.exam_result || '',
        score_sheet: record.score_sheet || '',
        cert_image: record.cert_image || ''
      }
      this.completeVisible = true
    },
    handleCompleteSubmit () {
      if (!this.completeForm.contact_id.trim()) {
        this.$message.warning('请输入官方 Contact ID')
        return
      }
      if (!this.completeForm.assigned_email.trim()) {
        this.$message.warning('请输入官方代报绑定的邮箱')
        return
      }

      const params = {
        id: this.activeRecord.id,
        contact_id: this.completeForm.contact_id.trim(),
        assigned_email: this.completeForm.assigned_email.trim(),
        exam_date: this.completeForm.exam_date.trim(),
        exam_result: this.completeForm.exam_result.trim(),
        score_sheet: this.completeForm.score_sheet.trim(),
        cert_image: this.completeForm.cert_image.trim()
      }

      this.completeLoading = true
      completeExamApply(params)
        .then(() => {
          this.completeLoading = false
          this.completeVisible = false
          this.$message.success('数据回填登记成功，信息已同步前台考生')
          this.fetchData()
        })
        .catch(err => {
          this.completeLoading = false
          console.error(err)
          this.$message.error('登记失败')
        })
    },
    handleDelete (id) {
      delExamApply({ id })
        .then(() => {
          this.$message.success('删除成功')
          this.fetchData()
        })
        .catch(err => {
          console.error(err)
          this.$message.error('删除失败')
        })
    },
    exportToExcel () {
      const listToExport = this.dataSource.filter(item => item.status === 1)
      if (listToExport.length === 0) {
        this.$message.warning('当前页没有处于“代报中”的订单可供导出！请切换“代报中”状态进行查询后导出')
        return
      }

      const headers = [
        'Family Name (姓)',
        'Given Name (名)',
        'Chinese Name (中文姓名)',
        'Gender (性别)',
        'Date of Birth (出生日期)',
        'ID Type (证件类型)',
        'ID Number (证件号码)',
        'Subject (报考类型)',
        'Instrument (报考乐器)',
        'Grade (级别)',
        'Contact Mobile (联系电话)'
      ]

      let csvContent = '\ufeff' + headers.join(',') + '\n'

      listToExport.forEach(item => {
        const names = item.name_en.trim().split(/\s+/)
        const lastName = names[0] || ''
        const firstName = names.slice(1).join(' ') || ''

        const row = [
          `"${lastName}"`,
          `"${firstName}"`,
          `"${item.name_zh}"`,
          `"${item.gender === 1 ? 'Male' : 'Female'}"`,
          `"${item.birthday}"`,
          `"${this.getIdTypeText(item.id_type)}"`,
          `"${item.id_number}"`,
          `"${item.exam_type === 1 ? 'Theory' : 'Performance'}"`,
          `"${item.instrument}"`,
          `"${item.exam_level}"`,
          `"${item.mobile}"`
        ]
        csvContent += row.join(',') + '\n'
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `ABRSM_Online_Registration_Batch_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.$message.success('已成功导出官方格式批量填报表 (CSV文件)')
    },
    formatTime (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${d} ${hh}:${mm}`
    },
    viewVideo (record) {
      this.activeRecord = record
      this.videoVisible = true
    },
    viewSongList (record) {
      this.activeRecord = record
      this.songListVisible = true
    },
    copyToClipboard (text) {
      if (!text) return
      const input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('已复制视频网盘地址到剪切板')
    }
  }
}
</script>

<style scoped>
.audit-modal-body {
  padding: 10px 0;
}

.media-container {
  border-right: 1px solid #e8e8e8;
  padding-right: 20px;
}

.media-section-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.audit-img {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  transition: all 0.3s;
}

.audit-img:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.media-tip {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
  text-align: center;
}

.cert-missing-alert {
  background-color: #fff2f0;
  border: 1px dashed #ff4d4f;
  color: #ff4d4f;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  font-size: 12px;
  margin-top: 10px;
}

.info-container {
  padding-left: 10px;
}

.info-header {
  font-size: 15px;
  font-weight: bold;
  border-bottom: 2px solid #ff5f4e;
  padding-bottom: 8px;
  margin-bottom: 16px;
  color: #222;
}

.info-row {
  margin-bottom: 14px;
}

.info-row .label {
  font-size: 12px;
  color: #666;
  margin-bottom: 3px;
}

.info-row .val {
  font-size: 14px;
  color: #111;
}

.info-row .val.highlight {
  font-size: 20px;
  font-weight: bold;
  color: #ff5f4e;
}

.info-row .val.highlight.pinyin {
  font-family: Consolas, Monaco, monospace;
}

.info-row .val-tip {
  font-size: 11px;
  color: #888;
}

.audit-action-area {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.reject-input-area {
  margin-top: 16px;
  background-color: #fafafa;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}
</style>
